/* hs-eslint ignored failing-rules */
/* eslint-disable react-hooks/exhaustive-deps */

'use es6';

import PropTypes from 'prop-types';
import { Fragment, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { CardSection } from 'foundations-components/transitional/components/Card';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import * as RequestState from 'marketplace-ui-common/requestStates/RequestState';
import Rating from 'marketplace-ui-reviews/components/Rating';
import { useReviewability } from 'marketplace-ui-reviews/hooks/useReviewability';
import { useReviews } from 'marketplace-ui-reviews/hooks/useReviews';
import { useGetPreviousReviews } from 'marketplace-ui-reviews/hooks/useGetPreviousReviews';
import { ProfileListing } from 'marketplace-ui-solutions-core/records/ProfileListing';
import { getAppContextCoreType, getIsPublicApp } from '../../selectors/app';
import { getUserId } from '../../selectors/auth';
import { replace } from '../../utils/history';
import ProfileCardWrapper from './ProfileCardWrapper';
import { ProfileReviewWizard } from './ProfileReviewWizard';
import ReviewsCardBody from './ReviewsCardBody';
import WriteReviewButton from './WriteReviewButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const I18N_BASE = 'ecosystem-marketplace-solutions-ui.profile.reviews';
const ReviewsCard = ({
  isPreview,
  onFiltersUpdate,
  onTrackInteraction,
  profileListing,
  reviewButtonRef,
  reviewFilters,
  reviewSummary,
  scrollToTabSection
}) => {
  const {
    pathname
  } = useLocation();
  const {
    name,
    slug
  } = profileListing;
  const isReviewLink = pathname.includes('/write-review');
  const [wizardOpen, setWizardOpen] = useState(isReviewLink);
  const [reviewPending, setReviewPending] = useState(false);
  const isPublicApp = useSelector(getIsPublicApp);
  const appContext = useSelector(getAppContextCoreType);
  const userId = useSelector(getUserId);
  const {
    reviews: editableReviews,
    reviewsFetchStatus: editableReviewsFetchStatus
  } = useGetPreviousReviews({
    appContext,
    entityId: profileListing.id,
    reviewType: 'PROFILE',
    reviewerUserId: userId
  });
  const editedReview = editableReviews[0];
  const {
    unreviewableReason,
    requestState: reviewabilityRequestStatus
  } = useReviewability({
    editPending: !!editedReview && reviewPending,
    entityId: profileListing.id,
    entityPortalId: profileListing.portalId,
    isPublicApp,
    reviewPending: !editedReview && reviewPending,
    reviewType: 'PROFILE'
  });
  const reviewabilityStatus = isPublicApp || isPreview ? RequestState.SUCCEEDED : reviewabilityRequestStatus;
  const {
    reviews,
    reviewStats,
    reviewsFetchStatus,
    reviewStatsFetchStatus,
    reviewsTotal,
    reviewsCount
  } = useReviews({
    appContext,
    reviewFilters,
    offeringId: profileListing.id,
    reviewType: 'PROFILE'
  });
  const renderedReviewButton = /*#__PURE__*/_jsx(WriteReviewButton, {
    hasEditableReview: !isPublicApp && !!editedReview,
    isPreview: isPreview,
    isPublicApp: isPublicApp,
    profileListing: profileListing,
    reviewButtonRef: reviewButtonRef,
    setWizardOpen: setWizardOpen,
    unreviewableReason: unreviewableReason
  });
  const renderedBody = useMemo(() => reviewSummary !== null && reviewSummary !== void 0 && reviewSummary.get('reviewCount') ? /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: reviewStatsFetchStatus,
    children: /*#__PURE__*/_jsx(ReviewsCardBody, {
      reviews: reviews,
      reviewStats: reviewStats,
      reviewsFetchStatus: reviewsFetchStatus,
      reviewsTotal: reviewsTotal,
      reviewsCount: reviewsCount,
      reviewFilters: reviewFilters,
      onFiltersUpdate: onFiltersUpdate,
      onTrackInteraction: onTrackInteraction,
      scrollToTabSection: scrollToTabSection
    })
  }) : /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h4",
      paddingBlockStart: "800",
      paddingBlockEnd: "400",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_BASE}.emptyState`
      })
    }), /*#__PURE__*/_jsx(Rating, {
      rating: 0,
      labelSize: "large",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_BASE}.reviewCount`,
        options: {
          count: 0
        }
      })
    }), /*#__PURE__*/_jsx(View, {
      paddingBlockStart: "800",
      children: renderedReviewButton
    })]
  }), [onTrackInteraction, profileListing, renderedReviewButton, reviewSummary, reviewStatsFetchStatus, scrollToTabSection]);
  const {
    headingWrapperProps
  } = useResponsiveProps({
    headingWrapperProps: {
      xs: {
        justifyContent: 'space-between',
        flexDirection: 'column',
        alignItems: 'start',
        rowGap: '800'
      },
      md: {
        justifyContent: 'space-between',
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: '800'
      }
    }
  }, true);
  const renderedHeading = useMemo(() => /*#__PURE__*/_jsxs(Flex, Object.assign({}, headingWrapperProps, {
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h2",
      typography: "heading-300",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_BASE}.title`,
        options: {
          name
        }
      })
    }), reviewSummary !== null && reviewSummary !== void 0 && reviewSummary.get('reviewCount') ? renderedReviewButton : null]
  })), [headingWrapperProps, name, reviewSummary, renderedReviewButton]);
  return /*#__PURE__*/_jsxs(LoadingWrapper, {
    requestStatus: reviewabilityStatus,
    children: [/*#__PURE__*/_jsx(ProfileCardWrapper, {
      id: "reviews",
      dataTestId: "profile-reviews-card",
      children: /*#__PURE__*/_jsxs(CardSection, {
        children: [renderedHeading, renderedBody]
      })
    }), wizardOpen && /*#__PURE__*/_jsx(ProfileReviewWizard, {
      editedReview: editedReview,
      editableReviewsFetchStatus: editableReviewsFetchStatus,
      onConfirm: () => {
        setReviewPending(true);
        replace(`/${slug}`);
        setWizardOpen(false);
      },
      onClose: () => {
        replace(`/${slug}`);
        setWizardOpen(false);
      },
      onTrackInteraction: onTrackInteraction,
      profileListing: profileListing,
      unreviewableReason: unreviewableReason
    })]
  });
};
ReviewsCard.propTypes = {
  isPreview: PropTypes.bool.isRequired,
  onFiltersUpdate: PropTypes.func.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  profileListing: PropTypes.instanceOf(ProfileListing),
  reviewButtonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]),
  reviewFilters: PropTypes.object,
  reviewSummary: PropTypes.object,
  scrollToTabSection: PropTypes.func.isRequired
};
export default ReviewsCard;